import { useCallback, useEffect, useState } from "react";

import Button from "~/components/buttons/Button";
import { ComboBoxPlain } from "~/components/formElements/ComboBox/ComboBox.tsx";
import Label from "~/components/formElements/Label";
import Hint from "~/components/Hint";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import SidebarContent from "~/components/Sidebar/components/SidebarContent";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline";
import SelectableQuotaProjectPhase
	from "~/modules/client/components/ClientDetailsView/components/QuotasTabContent/components/CreateQuotaSidebar/components/SelectableQuotaProjectPhase.tsx";
import { useProjectsProjectPhasesForQuotaAssignment } from "~/modules/project/api/projectPhase/projectPhaseQueries.ts";
import type { ProjectPhaseForQuotaAssignment } from "~/modules/project/api/projectPhase/projectPhaseTypes.ts";
import useClientsProjectSelectOptions from "~/modules/project/hooks/useClientsProjectSelectOptions.ts";
import type { DispatchStateAction } from "~/types/hookTypes.ts";

type Props = {
	clientId: string;
	excludedPhaseIds?: string[];
	onCancel: () => void;
	selectedPhasesData: ProjectPhaseForQuotaAssignment[]
	setSelectedPhasesData: DispatchStateAction<ProjectPhaseForQuotaAssignment[]>
};

const QuotaProjectPhaseSelector = ({ clientId, excludedPhaseIds, onCancel, setSelectedPhasesData }: Props) => {
	const [selectedProjectId, setSelectedProjectId] = useState<string>("");
	const [selectedPhaseIds, setSelectedPhaseIds] = useState<string[]>([]);
	const projectSelectOptions = useClientsProjectSelectOptions(clientId);

	const {
		data: projectPhasesData,
		isLoading: projectPhasesIsLoading,
	} = useProjectsProjectPhasesForQuotaAssignment(selectedProjectId);

	useEffect(() => {
		setSelectedPhaseIds([]);
	}, [selectedProjectId]);

	const selectablePhases = projectPhasesData ? projectPhasesData.filter(phase => {
		return !phase.hasMultipleOrders && !phase.hasInvoice && !excludedPhaseIds?.includes(phase.id);
	}) : [];

	const handlePhaseClick = useCallback((phaseId: string) => {
		setSelectedPhaseIds(prevState => {
			if (prevState.includes(phaseId)) {
				return prevState.filter(id => id !== phaseId);
			} else {
				return [...prevState, phaseId];
			}
		});
	}, []);

	const handleSubmit = () => {
		const selectedPhases = selectablePhases.filter(phase => selectedPhaseIds.includes(phase.id));
		if (selectedPhases.length > 0) {
			setSelectedPhasesData(prevState => {
				const newState = [...prevState];
				selectedPhases.forEach(phase => {
					if (!newState.find(prevStateSelectedPhase => prevStateSelectedPhase.id === phase.id)) {
						newState.push(phase);
					}
				});
				return newState;
			});
		}
		onCancel();
	};

	return <>
		<SidebarHeader>
			<SidebarHeaderHeadline>Projektphase auswählen</SidebarHeaderHeadline>
		</SidebarHeader>
		<SidebarContent>
			{projectPhasesIsLoading && <SidebarBusyOverlay />}
			<ComboBoxPlain optionsData={projectSelectOptions}
						   allowNew={false}
						   label="Projekt"
						   placeholder="Bitte Projekt auswählen"
						   onChange={(value) => setSelectedProjectId(typeof value === "string" ? value : "")}
						   value={selectedProjectId} />
			<div className="mt-4">
				<Label>Phasen</Label>
				<div className="mt-1 space-y-2">
					{!projectPhasesIsLoading && selectablePhases && selectablePhases.length > 0 && selectablePhases.map((phase) => {
						return <SelectableQuotaProjectPhase key={phase.id}
															onSelect={handlePhaseClick}
															isSelected={selectedPhaseIds.includes(phase.id)}
															phaseData={phase} />;
					})}
					{!selectedProjectId &&
						<Hint><p>Um eine oder mehrere Phase zuweisen, muss zuerst ein Projekt und anschließend die
							gewünschte/n Phase/n auswählen.</p>
							<br />
							<p>Es können nur Phasen mit folgenden Eigenschaften ausgewählt werden:</p>
							<ul className="list-inside list-disc">
								<li>Die Phase ist noch keinem Abrechnungskontingent zugewiesen.</li>
								<li>Die Phase enthält noch keine gestellten Rechnungen.</li>
								<li>Die Phase enthält maximal eine Bestellung.</li>
							</ul>
						</Hint>}
					{selectedProjectId && !projectPhasesIsLoading && selectablePhases.length === 0 &&
						<Hint>
							<p>Dieses Projekt enthält keine Phasen, die den Kriterien für eine Zuweisung zu einem
								Abrechnungskontingent entsprechen.</p>
						</Hint>}
				</div>
			</div>
		</SidebarContent>
		<SidebarFooter>
			<Button
				disabled={selectedPhaseIds.length === 0}
				onClick={handleSubmit}
			>
				{`${selectedPhaseIds.length > 0 ? selectedPhaseIds.length + " " : ""} Phase${selectedPhaseIds.length > 1 ? "n" : ""} auswählen `}
			</Button>
			<Button theme="white"
					onClick={onCancel}>
				Zurück
			</Button>
		</SidebarFooter>
	</>;
};

export default QuotaProjectPhaseSelector;